import { ChakraProvider } from '@chakra-ui/react'
import { i18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { cs, de, en, hu, it, ro, sk } from 'make-plural/plurals'
import { configure } from 'mobx'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useEffect } from 'react'
import { Provider } from 'urql'
import { client } from '../src/config/api'
import { messages as messagesCS } from '../src/locales/cs/messages.js'
import { messages as messagesDE } from '../src/locales/de/messages.js'
import { messages as messagesEN } from '../src/locales/en/messages.js'
import { messages as messagesHU } from '../src/locales/hu/messages.js'
import { messages as messagesIT } from '../src/locales/it/messages.js'
import { messages as messagesRO } from '../src/locales/ro/messages.js'
import { messages as messagesSK } from '../src/locales/sk/messages.js'
import { AppStore } from '../src/stores/AppStore'
import { Fonts } from '../src/styles/Fonts'
import { GA_TRACKING_ID, pageView } from '../src/utils/gtag'
import { getChakraTheme, getThemeLocale } from '../src/utils/theme/themeUtils'

configure({ enforceActions: 'never' })

i18n.loadLocaleData('en', { plurals: en })
i18n.loadLocaleData('it', { plurals: it })
i18n.loadLocaleData('hu', { plurals: hu })
i18n.loadLocaleData('ro', { plurals: ro })
i18n.loadLocaleData('de', { plurals: de })
i18n.loadLocaleData('sk', { plurals: sk })
i18n.loadLocaleData('cs', { plurals: cs })

i18n.load({
  en: messagesEN,
  it: messagesIT,
  hu: messagesHU,
  de: messagesDE,
  sk: messagesSK,
  cs: messagesCS,
  ro: messagesRO,
})

i18n.activate(getThemeLocale())
AppStore.activeLocale = getThemeLocale()

export default function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter()

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      pageView(url)
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => router.events.off('routeChangeComplete', handleRouteChange)
  }, [router.events])

  return (
    <>
      {GA_TRACKING_ID && (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
          />
          <Script
            id="gtag-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GA_TRACKING_ID}', {
                  page_path: window.location.pathname,
                });
            `,
            }}
          />
        </>
      )}
      <Provider value={client}>
        <I18nProvider i18n={i18n}>
          <ChakraProvider theme={getChakraTheme()}>
            <Fonts />
            <Component {...pageProps} />
          </ChakraProvider>
        </I18nProvider>
      </Provider>
    </>
  )
}
